<template>
    <BaseDialog v-model:open="open" :title="heading" show-close float-close>
        <div class="flex flex-col p-6 md:w-125" data-testid="sharing-intro-dialog">
            <h2 class="title-section text-valence-grey-850 pr-8">{{ heading }}</h2>

            <div class="flex items-center gap-3 rounded-lgr bg-valence-primary-100 p-4 mt-5">
                <span class="grid size-11 shrink-0 place-items-center rounded-xl bg-valence-primary-500 text-white">
                    <i class="bi text-xl/none" :class="calloutIcon" aria-hidden="true"></i>
                </span>
                <p class="body-small text-valence-grey-750 min-w-0">{{ calloutText }}</p>
            </div>

            <p class="body text-valence-grey-750 mt-4">
                To change who can see it, use the <span class="body-bold text-valence-grey-850">{{ shareButtonText }}</span> button at the top right of your profile.
                <template v-if="alternativeAccessCopy">You can {{ alternativeAccessCopy }} at any time.</template>
            </p>

            <HorizontalDivider />

            <BaseButton theme="primary" class="self-end mt-auto" @click="open = false">Got it</BaseButton>
        </div>
    </BaseDialog>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import { ACCESS, ACCESS_BUTTON_LABELS } from "~vue/utils/constants";
import { computed } from "vue";

const { access, availableAccessLevels } = defineProps<{ access: ACCESS; availableAccessLevels: Set<string> }>();

const open = defineModel<boolean>("open", { default: false });

const HEADINGS: Record<ACCESS, string> = {
    [ACCESS.ORGANIZATION]: "Your Collaboration Profile is discoverable by your organization",
    [ACCESS.INVITED_ORG_MEMBERS]: "Your Collaboration Profile is currently visible to colleagues you invite",
    [ACCESS.PRIVATE]: "Your Collaboration Profile is currently private",
};

const CALLOUT_TEXT: Record<ACCESS, string> = {
    [ACCESS.ORGANIZATION]: "New Collaboration Profiles are discoverable by default, so colleagues in your organization can find your profile and learn how you work best.",
    [ACCESS.INVITED_ORG_MEMBERS]: "New Collaboration Profiles start invite-only, so only the colleagues you share a link with can see how you work best.",
    [ACCESS.PRIVATE]: "New Collaboration Profiles start private, so only you can see it until you decide to share it.",
};

const CALLOUT_ICONS: Record<ACCESS, string> = {
    [ACCESS.ORGANIZATION]: "bi-people-fill",
    [ACCESS.INVITED_ORG_MEMBERS]: "bi-person-plus-fill",
    [ACCESS.PRIVATE]: "bi-lock-fill",
};

const ALTERNATIVE_COPY: [ACCESS, string][] = [
    [ACCESS.INVITED_ORG_MEMBERS, "switch to invite-only"],
    [ACCESS.ORGANIZATION, "make your profile discoverable to your organization"],
    [ACCESS.PRIVATE, "make it private"],
];

const heading = computed(() => HEADINGS[access]);
const calloutText = computed(() => CALLOUT_TEXT[access]);
const calloutIcon = computed(() => CALLOUT_ICONS[access]);
const shareButtonText = computed(() => ACCESS_BUTTON_LABELS[access]);

const alternativeAccessCopy = computed(() =>
    ALTERNATIVE_COPY.filter(([level]) => level !== access && availableAccessLevels.has(level))
        .map(([, copy]) => copy)
        .join(" or "),
);
</script>
